<template>
  <div class="inspection-template-manage">
    <!-- 搜索栏 -->
    <div class="search-bar">
      <el-select
        v-model="searchForm.category"
        placeholder="请选择分类"
        style="width: 200px; margin-right: 10px"
        clearable
        filterable
      >
        <el-option label="全部分类" value="" />
        <el-option
          v-for="category in categoryList"
          :key="category"
          :label="category"
          :value="category"
        />
      </el-select>
      <el-select
        v-model="searchForm.status"
        placeholder="请选择状态"
        style="width: 150px; margin-right: 10px"
        clearable
      >
        <el-option label="全部状态" value="" />
        <el-option label="启用" :value="1" />
        <el-option label="禁用" :value="0" />
      </el-select>
      <el-input
        v-model="searchForm.name"
        placeholder="请输入项目名称"
        style="width: 200px; margin-right: 10px"
        clearable
      />
      <el-button type="primary" @click="handleSearch">搜索</el-button>
      <el-button @click="handleReset">重置</el-button>
      <el-button type="success" @click="handleAdd">新增</el-button>
      <el-button type="danger" @click="handleBatchDelete" :disabled="selectedRows.length === 0">批量删除</el-button>
    </div>
    
    <!-- 数据表格 -->
    <el-table 
      :data="tableData" 
      border 
      style="width: 100%; margin-top: 20px"
      @selection-change="handleSelectionChange"
    >
      <el-table-column type="selection" width="55" />
      <el-table-column prop="templateId" label="模板ID" width="150" />
      <el-table-column prop="name" label="项目名称" width="200" />
      <el-table-column prop="category" label="分类" width="150" />
      <el-table-column prop="description" label="描述" show-overflow-tooltip />
      <el-table-column prop="sortOrder" label="排序" width="80" />
      <el-table-column prop="status" label="状态" width="100">
        <template slot-scope="scope">
          <el-tag v-if="scope.row.status === 1" type="success">启用</el-tag>
          <el-tag v-else type="danger">禁用</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="createTime" label="创建时间" width="180">
        <template slot-scope="scope">
          {{ formatDate(scope.row.createTime) }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="250" fixed="right">
        <template slot-scope="scope">
          <el-button size="mini" type="primary" @click="handleEdit(scope.row)">编辑</el-button>
          <el-button 
            size="mini" 
            :type="scope.row.status === 1 ? 'warning' : 'success'" 
            @click="handleToggleStatus(scope.row)"
          >
            {{ scope.row.status === 1 ? '禁用' : '启用' }}
          </el-button>
          <el-button size="mini" type="danger" @click="handleDelete(scope.row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>
    
    <!-- 分页 -->
    <el-pagination
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
      :current-page="pagination.current"
      :page-sizes="[10, 20, 50, 100]"
      :page-size="pagination.size"
      layout="total, sizes, prev, pager, next, jumper"
      :total="pagination.total"
      style="margin-top: 20px; text-align: right"
    />
    
    <!-- 新增/编辑对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="600px">
      <el-form :model="form" :rules="rules" ref="form" label-width="120px">
        <el-form-item label="项目名称" prop="name">
          <el-input v-model="form.name" placeholder="请输入项目名称" />
        </el-form-item>
        <el-form-item label="分类" prop="category">
          <el-select v-model="form.category" placeholder="请选择分类" style="width: 100%" filterable allow-create>
            <el-option
              v-for="category in categoryList"
              :key="category"
              :label="category"
              :value="category"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input
            v-model="form.description"
            type="textarea"
            :rows="3"
            placeholder="请输入描述"
          />
        </el-form-item>
        <el-form-item label="排序" prop="sortOrder">
          <el-input-number v-model="form.sortOrder" :min="0" style="width: 100%" />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-switch
            v-model="form.status"
            :active-value="1"
            :inactive-value="0"
            active-text="启用"
            inactive-text="禁用"
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="handleSubmit">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { 
  getTemplateList, 
  addTemplate, 
  updateTemplate, 
  deleteTemplate, 
  batchDeleteTemplates,
  toggleTemplateStatus,
  getCategories
} from '@/api/inspectionTemplate'

export default {
  name: 'InspectionTemplateManage',
  data() {
    return {
      searchForm: {
        category: '',
        status: '',
        name: ''
      },
      categoryList: [],
      tableData: [],
      selectedRows: [],
      pagination: {
        current: 1,
        size: 10,
        total: 0
      },
      dialogVisible: false,
      dialogTitle: '新增检测项模板',
      form: {
        name: '',
        category: '',
        description: '',
        sortOrder: 0,
        status: 1
      },
      rules: {
        name: [
          { required: true, message: '请输入项目名称', trigger: 'blur' }
        ],
        category: [
          { required: true, message: '请选择分类', trigger: 'change' }
        ],
        sortOrder: [
          { required: true, message: '请输入排序', trigger: 'blur' }
        ]
      },
      isEdit: false,
      editId: null
    }
  },
  mounted() {
    this.loadCategories()
    this.loadData()
  },
  methods: {
    // 加载分类列表
    async loadCategories() {
      try {
        const response = await getCategories()
        if (response.code === '200') {
          this.categoryList = response.data || []
        } else {
          console.error('加载分类列表失败:', response.msg)
        }
      } catch (error) {
        console.error('加载分类列表失败:', error)
      }
    },
    
    // 加载数据
    async loadData() {
      try {
        const params = {
          current: this.pagination.current,
          size: this.pagination.size,
          category: this.searchForm.category,
          status: this.searchForm.status,
          name: this.searchForm.name
        }
        const response = await getTemplateList(params)
        if (response.code === '200') {
          this.tableData = response.data.records
          this.pagination.total = response.data.total
        } else {
          this.$message.error(response.msg || '获取数据失败')
        }
      } catch (error) {
        console.error('加载数据失败:', error)
        this.$message.error('网络错误，请重试')
      }
    },
    
    // 搜索
    handleSearch() {
      this.pagination.current = 1
      this.loadData()
    },
    
    // 重置
    handleReset() {
      this.searchForm.category = ''
      this.searchForm.status = ''
      this.searchForm.name = ''
      this.pagination.current = 1
      this.loadData()
    },
    
    // 新增
    handleAdd() {
      this.isEdit = false
      this.dialogTitle = '新增检测项模板'
      this.form = {
        name: '',
        category: '',
        description: '',
        sortOrder: 0,
        status: 1
      }
      this.dialogVisible = true
    },
    
    // 编辑
    handleEdit(row) {
      this.isEdit = true
      this.editId = row.id
      this.dialogTitle = '编辑检测项模板'
      this.form = { ...row }
      this.dialogVisible = true
    },
    
    // 删除
    handleDelete(row) {
      this.$confirm('确认删除该检测项模板吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await deleteTemplate(row.id)
          if (response.code === '200') {
            this.$message.success('删除成功')
            this.loadData()
          } else {
            this.$message.error(response.msg || '删除失败')
          }
        } catch (error) {
          console.error('删除失败:', error)
          this.$message.error('网络错误，请重试')
        }
      })
    },
    
    // 批量删除
    handleBatchDelete() {
      if (this.selectedRows.length === 0) {
        this.$message.warning('请选择要删除的项目')
        return
      }
      
      this.$confirm(`确认删除选中的 ${this.selectedRows.length} 个检测项模板吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const ids = this.selectedRows.map(row => row.id)
          const response = await batchDeleteTemplates(ids)
          if (response.code === '200') {
            this.$message.success('批量删除成功')
            this.loadData()
          } else {
            this.$message.error(response.msg || '批量删除失败')
          }
        } catch (error) {
          console.error('批量删除失败:', error)
          this.$message.error('网络错误，请重试')
        }
      })
    },
    
    // 切换状态
    handleToggleStatus(row) {
      const action = row.status === 1 ? '禁用' : '启用'
      this.$confirm(`确认${action}该检测项模板吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await toggleTemplateStatus(row.id)
          if (response.code === '200') {
            this.$message.success(`${action}成功`)
            this.loadData()
          } else {
            this.$message.error(response.msg || `${action}失败`)
          }
        } catch (error) {
          console.error(`${action}失败:`, error)
          this.$message.error('网络错误，请重试')
        }
      })
    },
    
    // 选择变化
    handleSelectionChange(selection) {
      this.selectedRows = selection
    },
    
    // 提交表单
    handleSubmit() {
      this.$refs.form.validate(async (valid) => {
        if (valid) {
          try {
            let response
            if (this.isEdit) {
              response = await updateTemplate(this.form)
            } else {
              response = await addTemplate(this.form)
            }
            
            if (response.code === '200') {
              this.$message.success(this.isEdit ? '更新成功' : '添加成功')
              this.dialogVisible = false
              this.loadData()
              // 如果是新增且分类不在列表中，重新加载分类列表
              if (!this.isEdit && !this.categoryList.includes(this.form.category)) {
                this.loadCategories()
              }
            } else {
              this.$message.error(response.msg || (this.isEdit ? '更新失败' : '添加失败'))
            }
          } catch (error) {
            console.error('提交失败:', error)
            this.$message.error('网络错误，请重试')
          }
        }
      })
    },
    
    // 格式化日期
    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleString('zh-CN')
    },
    
    // 分页大小改变
    handleSizeChange(size) {
      this.pagination.size = size
      this.loadData()
    },
    
    // 当前页改变
    handleCurrentChange(current) {
      this.pagination.current = current
      this.loadData()
    }
  }
}
</script>

<style scoped>
.inspection-template-manage {
  padding: 20px;
}

.search-bar {
  margin-bottom: 20px;
}
</style>
